import React from 'react';
import styled, {css} from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import {graphql} from 'gatsby';
import {documentToReactComponents} from '@contentful/rich-text-react-renderer';

import {HealthInsuranceGraphicIcon, DiscountGraphicIcon, WellnessScoreGraphicIcon} from '@nib/icons';
import {Section, Stack, Columns, Column} from '@nib/layout';

import Alert from '@nib-components/alert';
import Copy, {Italic} from '@nib-components/copy';
import Heading from '@nib-components/heading';
import Link from '@nib-components/link';
import HeroPanel from '@nib-components/hero-panel';
import FeaturePanel from '@nib-components/feature-panel';
import {PrimaryButton} from '@nib-components/button';
import {colorTrueGreen, copyColor, copyFontFamily, copyFontSize, copyLineHeight, h3FontFamily, h3FontSize, h3LineHeight, h3Tracking, headingColor} from '@nib-components/theme';

import Layout from '../components/Layout';
import VerticalFeaturePanel from '../components/VerticalFeaturePanel';
import UtilityButtons from '../components/UtilityButtons';
import BlogPosts from '../components/BlogPosts';
import metrics from '../metrics';

import contentfulMarkdownOptions from '../utils/contentfulMarkdownOptions';
import config from '../utils/env';

// images
import DesktopHero from '../img/hero-panel/desktop.jpg';
import DesktopHero2x from '../img/hero-panel/desktop@2x.jpg';
import MobileHero from '../img/hero-panel/mobile.jpg';
import MobileHero2x from '../img/hero-panel/mobile@2x.jpg';
import TabletHero from '../img/hero-panel/tablet.jpg';
import TabletHero2x from '../img/hero-panel/tablet@2x.jpg';
import DedicatedSupportImg from '../img/dedicated_support.jpg';

export const landingPageQuery = graphql`
  query {
    allContentfulBlogArticle(limit: 3, sort: {fields: updatedAt, order: DESC}) {
      edges {
        node {
          id
          createdAt
          heroImage {
            file {
              url
            }
          }
          thumbnail {
            title
            file {
              url
              fileName
            }
          }
          title
          urlSlug
          updatedAt
          sectionOneHeading
          sectionOneContent {
            sectionOneContent
          }
        }
      }
    }
    contentfulContactUsSection(slug: {eq: "first-choice-contact-us"}) {
      name
      slug
      title
      description {
        description
      }
      contactInfo {
        email
        extraInfo
        methodName
        name
        type
        phoneNumber
      }
    }
    contentfulSimpleSection(slug: {eq: "nib-first-choice-network"}) {
      name
      slug
      title
      text {
        raw
      }
    }
    contentfulSimpleWebsiteContent(urlSlug: {eq: "provider-announcement"}, isDisplay: {eq: true}) {
      startDate
      endDate
      pageContent {
        pageContent
      }
    }
  }
`;

const SectionWithImage = styled(Section)`
  position: relative;

  ${({img}) =>
    img &&
    css`
      background-repeat: no-repeat;
      background-size: auto 100%;
      background-position: 66vw;

      ${breakpoint('md')`
        background-image: url(${img});
      `};
    `}
`;

const SideImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 33%;
  display: none;

  ${breakpoint('lg')`
    display: block;
  `};

  ${({img}) =>
    img &&
    css`
      background-repeat: no-repeat;
      background-size: cover;
      background-image: url(${img});
    `}
`;

const HeroPanelCopy = styled.div`
  color: ${copyColor};
  font-family: ${copyFontFamily};
  font-size: ${copyFontSize};
  line-height: ${copyLineHeight};
  ${breakpoint('md')`
    color: ${headingColor};
    font-family: ${h3FontFamily};
    font-size: ${h3FontSize};
    letter-spacing: ${h3Tracking};
    line-height: ${h3LineHeight};
  `};
`;

const StyledFeaturePanel = styled(FeaturePanel)`
  padding: 0;

  h2,
  svg {
    color: ${colorTrueGreen};
  }
`;

const utilityLinks = [
  {
    title: 'Login',
    subtitle: 'Health Partner website',
    url: config.healthPartnerWebUrl
  },
  {
    title: 'Get started',
    subtitle: 'Register your interest',
    url: '/registration-form/',
    isGatsbyLink: true
  },
  {
    title: 'Contact nib',
    subtitle: 'Need help?',
    url: '#dedicated-support'
  },
  {
    title: 'Find out more',
    subtitle: 'First Choice network',
    url: config.healthPartnerDirectoryUrl
  }
];

const NotificationBanner = props => {
  const {content} = props;
  if (content === null) {
    return null;
  }
  const today = new Date();
  const from = new Date(content.startDate);
  const to = new Date(content.endDate);
  if (from > today || today > to) {
    return null;
  }
  const text = content.pageContent.pageContent;
  return (
    <Section>
      <Alert type="warning" title="Important announcement" fullWidth>
        <div className="message" dangerouslySetInnerHTML={{__html: text}} />
      </Alert>
    </Section>
  );
};

const IndexPage = ({data}) => {
  return (
    <Layout>
      {/*
        The empty div below is a placeholder fix for a routing issue when going directly to the registration page
        via the browser address bar. It seems to be caused by the way gatsby works and the form's javascript logic
        that uses the first div as the container for the form.
      */}
      <div />
      <HeroPanel
        images={{
          desktop: DesktopHero,
          desktop2x: DesktopHero2x,
          mobile: MobileHero,
          mobile2x: MobileHero2x,
          tablet: TabletHero,
          tablet2x: TabletHero2x
        }}
        title="nib Health Partners"
        variation="condensed"
      >
        <HeroPanelCopy>Working together to improve health outcomes for your nib and AA Health patients.</HeroPanelCopy>
        <PrimaryButton href={config.healthPartnerWebUrl}>Login</PrimaryButton>
      </HeroPanel>
      <Section background="warmWhite60">
        <UtilityButtons buttons={utilityLinks} />
      </Section>
      <NotificationBanner content={data.contentfulSimpleWebsiteContent} />
      <Section>
        <StyledFeaturePanel title="The benefits of being in nib’s First Choice network">
          <FeaturePanel.Feature icon={HealthInsuranceGraphicIcon}>Promotion of your services to our members on our directory</FeaturePanel.Feature>
          <FeaturePanel.Feature icon={DiscountGraphicIcon}>
            Your patients have 100% of their eligible costs covered<sup>1</sup>
          </FeaturePanel.Feature>
          <FeaturePanel.Feature icon={WellnessScoreGraphicIcon}>You can support your patient’s health journey by minimising their paperwork</FeaturePanel.Feature>
        </StyledFeaturePanel>
        <Copy small>
          <Link href="#need-to-know">
            Important things to know <sup>1</sup>
          </Link>
        </Copy>
      </Section>
      <Section background="sageGreen60">
        <Stack space={6}>
          <Heading color="trueGreen" size={{sm: 4, md: 3, lg: 2}} component="h2">
            {data.contentfulSimpleSection ? data.contentfulSimpleSection.title : 'What is the nib First Choice network'}
          </Heading>
          {documentToReactComponents(JSON.parse(data.contentfulSimpleSection.text.raw), contentfulMarkdownOptions)}
        </Stack>
      </Section>
      <Section>
        <VerticalFeaturePanel />
        <Copy small>
          <Link href="#need-to-know">
            Important things to know <sup>2 3</sup>
          </Link>
        </Copy>
      </Section>
      <SectionWithImage background="trueGreen">
        <a id="dedicated-support" />
        <Columns space={{xs: 2, lg: 4}} collapseBelow="md" align={{xs: 'center', lg: 'left'}} verticalAlign="top">
          <Column width="2/3">
            <Stack space={{xs: 4, md: 6}}>
              <Heading color="white" size={{sm: 4, md: 3, lg: 2}} component="h2">
                {data.contentfulContactUsSection.title || 'Dedicated Support'}
              </Heading>
              {data.contentfulContactUsSection.description && <Copy color="white">{data.contentfulContactUsSection.description.description}</Copy>}
              <Columns space={{xs: 2, lg: 4}} collapseBelow="md" align={{xs: 'center', lg: 'left'}} verticalAlign="top">
                {data.contentfulContactUsSection.contactInfo.map((info, index) => (
                  <Column width={`1/${data.contentfulContactUsSection.contactInfo.length}`} key={index}>
                    <Stack space={1}>
                      <Copy color="white">{info.methodName}</Copy>
                      {info.type === 'phone' ? (
                        <Link color="light" href={`tel:${info.phoneNumber}`}>
                          {info.phoneNumber}
                        </Link>
                      ) : (
                        <Link color="light" href={`mailto:${info.email}`}>
                          {info.email}
                        </Link>
                      )}
                      {info.extraInfo && (
                        <Italic color="white" small>
                          Monday to Friday 8:30am to 4:30pm
                        </Italic>
                      )}
                    </Stack>
                  </Column>
                ))}
              </Columns>
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>
        <SideImage img={DedicatedSupportImg} />
      </SectionWithImage>
      <Section background="warmWhite60">
        <BlogPosts />
      </Section>
      <Section>
        <a id="need-to-know" />
        <Stack space={4}>
          <Heading color="trueGreen" size={{sm: 4, md: 3, lg: 2}} component="h2">
            Important things to know
          </Heading>
          <Copy measure={false}>1. up to their benefit limit and in line with their policy terms</Copy>
          <Copy measure={false}>
            2. nib will collect the registration form information to assess if you are eligible to join the nib First Choice Network. If relevant information isn’t provided, we may not be able to
            assess your eligibility. For more information about how we hold information, please see our{' '}
            <Link href="https://www.nib.co.nz/privacy-policy" target="_blank">
              privacy policy
            </Link>
            .
          </Copy>
          <Copy measure={false}>
            3. The First Choice network and Health Partner website do not apply to all health services. Your details may still be listed in the directory as a health partner.
          </Copy>
        </Stack>
      </Section>
    </Layout>
  );
};

export default metrics({pageName: 'First choice landing'})(IndexPage);
